/* SIDE ARTS V3 — Minimal client JS
 * - No frameworks, no animation libs.
 * - Header scroll state, menu overlay, scroll-reveal, cart drawer hook.
 */
(() => {
  "use strict";

  // Localized UI strings (injected by layout/theme.liquid, locale-aware). FR fallbacks kept defensively.
  const S = (window.theme && window.theme.strings) || {};
  const ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";

  /* ---------- Brand cursor (values: <html data-cursor-*> in layout/theme.liquid) ----------
   * theme.css already shows the cursor from its CDN URL. Re-serving it as a blob URL
   * works around browsers that refuse a cross-origin CSS cursor image.
   */
  const cursorUrl = document.documentElement.dataset.cursorUrl;
  if (cursorUrl && window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
    fetch(cursorUrl, { mode: "cors" })
      .then((r) => (r.ok ? r.blob() : Promise.reject()))
      .then((blob) => {
        const hotspot = document.documentElement.dataset.cursorHotspot || "20 9";
        document.documentElement.style.setProperty("--brand-cursor", `url("${URL.createObjectURL(blob)}") ${hotspot}, auto`);
      })
      .catch(() => { /* keep the direct URL */ });
  }

  /* ---------- Focus + modality (shared: menu, cart drawer, welcome popup, size calc) ----------
   * Panels here are hidden by transform, not by display, so without `inert` their
   * links stay tabbable off-screen. Opening a panel also inerts everything outside
   * it (real modality), and closing gives focus back to whatever opened it.
   * Elements that must keep working behind an open panel — backdrops, the cart
   * live region — opt out with data-inert-skip.
   */
  const FOCUSABLE =
    'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  // Depth-counted so overlapping panels (drawer opened from a modal) restore exactly.
  const setOutsideInert = (panel, on) => {
    for (let node = panel; node && node.parentElement && node !== document.body; node = node.parentElement) {
      for (const sib of node.parentElement.children) {
        if (sib === node || sib.tagName === "SCRIPT" || sib.tagName === "TEMPLATE") continue;
        if (sib.hasAttribute("data-inert-skip")) continue;
        const depth = (parseInt(sib.dataset.inertDepth || "0", 10) || 0) + (on ? 1 : -1);
        if (depth > 0) {
          sib.dataset.inertDepth = String(depth);
          sib.setAttribute("inert", "");
        } else if (sib.dataset.inertDepth) {
          delete sib.dataset.inertDepth;
          sib.removeAttribute("inert");
        }
      }
    }
  };

  const panelReturn = new WeakMap();

  const openPanel = (panel) => {
    panelReturn.set(panel, document.activeElement);
    panel.removeAttribute("inert");
    panel.setAttribute("aria-hidden", "false");
    setOutsideInert(panel, true);
    // Focus synchronously: requestAnimationFrame never fires in a hidden or
    // background tab, which would leave the panel open with focus stranded
    // behind it — exactly the bug this is meant to fix.
    const first = panel.querySelector("[data-autofocus]") || panel.querySelector(FOCUSABLE);
    if (first) first.focus({ preventScroll: true });
  };

  // useInert: false for panels already removed from the tab order by CSS
  // (display:none / visibility:hidden) — adding inert there would also hide the
  // form from Shopify's bot protection.
  const closePanel = (panel, useInert) => {
    setOutsideInert(panel, false);
    const back = panelReturn.get(panel);
    panelReturn.delete(panel);
    if (back && document.contains(back) && typeof back.focus === "function") back.focus();
    panel.setAttribute("aria-hidden", "true");
    if (useInert !== false) panel.setAttribute("inert", "");
  };

  /* ---------- Header scroll state ---------- */
  const header = document.querySelector(".header");
  if (header) {
    let lastY = 0;
    const onScroll = () => {
      const y = window.scrollY;
      header.dataset.scrolled = y > 4 ? "true" : "false";
      lastY = y;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
  }

  /* ---------- Homepage hero-immersive: reveal header on first scroll ---------- */
  if (document.body.classList.contains("template-index")) {
    const reveal = () => {
      if (window.scrollY > 160) {
        document.body.classList.add("header-revealed");
        window.removeEventListener("scroll", reveal);
      }
    };
    window.addEventListener("scroll", reveal, { passive: true });
  }

  /* ---------- Hero slideshow: auto-swipe (markup: sections/hero.liquid) ---------- */
  const heroSlides = document.querySelector("[data-hero-slides]");
  if (heroSlides && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    const slides = heroSlides.querySelectorAll(".hero__slide");
    const interval = (parseInt(heroSlides.dataset.interval, 10) || 9) * 1000;
    let current = 0;
    if (slides.length > 1) {
      setInterval(() => {
        if (document.hidden) return;
        const prev = slides[current];
        current = (current + 1) % slides.length;
        const next = slides[current];
        // Snap the incoming slide back to the right edge (it may still sit at -100%
        // from its last exit) without animating across the screen, then swipe.
        next.style.transition = "none";
        next.classList.remove("is-leaving");
        void next.offsetWidth;
        next.style.transition = "";
        prev.classList.replace("is-active", "is-leaving");
        next.classList.add("is-active");
      }, interval);
    }
  }

  /* ---------- Menu overlay ---------- */
  const menuToggle = document.querySelector("[data-menu-toggle]");
  const menuCloseEls = document.querySelectorAll("[data-menu-close]");
  const overlay = document.querySelector(".menu-overlay");
  const backdrop = document.querySelector(".menu-backdrop");
  const setMenuOpen = (open) => {
    if (!overlay) return;
    if ((overlay.dataset.open === "true") === open) return;
    overlay.dataset.open = open ? "true" : "false";
    if (backdrop) backdrop.dataset.open = open ? "true" : "false";
    document.body.classList.toggle("no-scroll", open);
    if (menuToggle) menuToggle.setAttribute("aria-expanded", String(open));
    if (open) openPanel(overlay);
    else closePanel(overlay);
  };
  if (menuToggle) menuToggle.addEventListener("click", () => {
    const isOpen = overlay && overlay.dataset.open === "true";
    setMenuOpen(!isOpen);
  });
  menuCloseEls.forEach((el) => el.addEventListener("click", () => setMenuOpen(false)));
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") setMenuOpen(false);
  });

  /* ---------- Scroll reveal ---------- */
  // Elements crossing in together read as a row: stagger them in DOM order so a grid
  // fills left-to-right instead of snapping in as one block. Column-count agnostic,
  // so it holds at every breakpoint. A lone element never waits.
  const revealEls = document.querySelectorAll("[data-reveal]");
  if (revealEls.length && "IntersectionObserver" in window) {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const STAGGER_STEP = 45;
    const STAGGER_MAX = 180;
    const io = new IntersectionObserver(
      (entries) => {
        const shown = entries.filter((entry) => entry.isIntersecting);
        if (!shown.length) return;
        shown.sort((a, b) =>
          a.target.compareDocumentPosition(b.target) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
        );
        shown.forEach((entry, i) => {
          const el = entry.target;
          if (shown.length > 1 && i > 0 && !reduceMotion.matches) {
            el.style.transitionDelay = Math.min(i * STAGGER_STEP, STAGGER_MAX) + "ms";
            el.addEventListener("transitionend", () => { el.style.transitionDelay = ""; }, { once: true });
          }
          el.classList.add("is-visible");
          io.unobserve(el);
        });
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.05 }
    );
    revealEls.forEach((el) => io.observe(el));
  }

  /* ---------- Accordion (animated height, replaces native <details> snap) ---------- */
  document.querySelectorAll(".accordion__item").forEach((item) => {
    const summary = item.querySelector(":scope > summary");
    const body = item.querySelector(":scope > .accordion__body");
    if (!summary || !body) return;
    const ease = "cubic-bezier(0.16, 1, 0.3, 1)";
    const duration = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 90 : 260;
    let anim = null;
    summary.addEventListener("click", (e) => {
      e.preventDefault();
      if (item.classList.contains("is-animating")) return;
      item.open ? closeItem() : openItem();
    });
    function openItem() {
      item.style.overflow = "hidden";
      item.classList.add("is-animating");
      const startHeight = item.offsetHeight;
      item.open = true;
      const endHeight = summary.offsetHeight + body.scrollHeight;
      item.style.height = `${startHeight}px`;
      anim?.cancel();
      anim = item.animate({ height: [`${startHeight}px`, `${endHeight}px`] }, { duration, easing: ease });
      anim.onfinish = () => finish(true);
    }
    function closeItem() {
      item.style.overflow = "hidden";
      item.classList.add("is-animating");
      const startHeight = item.offsetHeight;
      const endHeight = summary.offsetHeight;
      anim?.cancel();
      anim = item.animate({ height: [`${startHeight}px`, `${endHeight}px`] }, { duration, easing: ease });
      anim.onfinish = () => finish(false);
    }
    function finish(isOpen) {
      item.open = isOpen;
      item.classList.remove("is-animating");
      item.style.height = "";
      item.style.overflow = "";
      anim = null;
    }
  });

  /* ---------- Cart drawer ---------- */
  const drawer = document.getElementById("cart-drawer");
  const drawerBackdrop = document.querySelector(".cart-drawer-backdrop");
  const drawerBody = drawer ? drawer.querySelector("[data-cart-body]") : null;
  const drawerFoot = drawer ? drawer.querySelector("[data-cart-foot]") : null;
  const drawerSubtotal = drawer ? drawer.querySelector("[data-cart-subtotal]") : null;
  const cartToggles = document.querySelectorAll("[data-cart-toggle]");

  const formatMoneyEUR = (cents) =>
    (cents / 100).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });

  // Cart mutations are silent for screen readers otherwise: the count, the
  // subtotal and the removed line all change without a word. Only real
  // mutations announce — a passive resync would just be noise.
  const cartStatusEl = document.querySelector("[data-cart-status]");
  const announceCart = (cart) => {
    if (!cartStatusEl || !cart) return;
    const tpl = S.cartUpdated || "Panier mis à jour : %N% article(s), sous-total %TOTAL%.";
    cartStatusEl.textContent = tpl
      .replace("%N%", String(cart.item_count || 0))
      .replace("%TOTAL%", formatMoneyEUR(cart.total_price || 0));
  };

  const updateShippingBars = (totalCents) => {
    const bars = document.querySelectorAll("[data-shipping-bar]");
    bars.forEach((bar) => {
      const threshold = parseInt(bar.dataset.threshold, 10) || 15000;
      const remaining = Math.max(0, threshold - totalCents);
      const pct = Math.min(100, Math.round((totalCents / threshold) * 100));
      const reached = remaining === 0;
      bar.dataset.reached = reached ? "true" : "false";
      const fill = bar.querySelector("[data-shipping-fill]");
      if (fill) fill.style.transform = `scaleX(${pct / 100})`;
      const msg = bar.querySelector("[data-shipping-msg]");
      if (msg) {
        const tplReached = bar.dataset.tplReached || "";
        const tplRemaining = bar.dataset.tplRemaining || "__AMOUNT__";
        msg.textContent = reached
          ? tplReached
          : tplRemaining.replace("__AMOUNT__", formatMoneyEUR(remaining));
      }
    });
  };

  const setDrawerOpen = (open) => {
    if (!drawer) return;
    if ((drawer.dataset.open === "true") === open) return;
    drawer.dataset.open = open ? "true" : "false";
    if (drawerBackdrop) drawerBackdrop.dataset.open = open ? "true" : "false";
    document.body.classList.toggle("no-scroll", open);
    cartToggles.forEach((b) => b.setAttribute("aria-expanded", String(open)));
    if (open) openPanel(drawer);
    else closePanel(drawer);
  };

  /* ---------- Cart: server-rendered sync (Sections API) ---------- */
  // Cart markup lives in Liquid (sections/cart-drawer.liquid + main-cart).
  // JS never builds cart markup — it swaps fragments of the freshly rendered
  // sections returned by the cart mutation endpoints instead.
  const DRAWER_SECTION = "cart-drawer";

  const syncFragment = (fresh, selector) => {
    const freshEl = fresh.querySelector(selector);
    const liveEl = drawer ? drawer.querySelector(selector) : null;
    if (!freshEl || !liveEl) return;
    liveEl.innerHTML = freshEl.innerHTML;
    liveEl.toggleAttribute("hidden", freshEl.hasAttribute("hidden"));
  };

  const swapDrawerFromHtml = (html) => {
    if (!drawer || !html) return;
    const fresh = document.createElement("div");
    fresh.innerHTML = html;
    syncFragment(fresh, "[data-cart-body]");
    syncFragment(fresh, "[data-bundle-slot]");
    syncFragment(fresh, "[data-cart-discounts]");
    const freshFoot = fresh.querySelector("[data-cart-foot]");
    if (drawerFoot && freshFoot) drawerFoot.toggleAttribute("hidden", freshFoot.hasAttribute("hidden"));
    const freshSubtotal = fresh.querySelector("[data-cart-foot] [data-cart-subtotal]");
    if (drawerSubtotal && freshSubtotal) drawerSubtotal.textContent = freshSubtotal.textContent;
  };

  // Apply cart JSON (counts, shipping bars) + rendered section HTML (drawer, cart page).
  const applyCartState = (cart, sections) => {
    if (cart) {
      const itemCount = cart.item_count || 0;
      document.querySelectorAll("[data-cart-count]").forEach((el) => {
        el.textContent = itemCount > 0 ? String(itemCount) : "";
        el.toggleAttribute("hidden", itemCount === 0);
      });
      updateShippingBars(cart.total_price || 0);
    }
    if (sections) {
      swapDrawerFromHtml(sections[DRAWER_SECTION]);
      const pageHtml = sections[cartPageSectionId()];
      if (pageHtml) swapCartPageSection(pageHtml);
    }
  };

  let toastEl = null;
  let toastTimer = null;
  const showCartToast = (msg) => {
    if (!toastEl) {
      toastEl = document.createElement("div");
      toastEl.className = "cart-toast";
      toastEl.setAttribute("role", "status");
      toastEl.setAttribute("aria-live", "polite");
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    toastEl.dataset.show = "true";
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { toastEl.dataset.show = "false"; }, 3200);
  };

  const escapeHtml = (s) =>
    String(s).replace(/[&<>"']/g, (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
    );

  const fetchCart = async () => {
    try {
      const res = await fetch("/cart.js", { headers: { Accept: "application/json" } });
      if (!res.ok) return null;
      return await res.json();
    } catch (_) { return null; }
  };

  // Sections to request alongside cart mutations: always the drawer,
  // plus the cart page section when we're on /cart.
  const cartSectionsParam = () =>
    onCartPage() ? `${DRAWER_SECTION},${cartPageSectionId()}` : DRAWER_SECTION;

  // Full resync from server truth (no mutation): cart JSON + rendered sections.
  const syncCartFromServer = async () => {
    const [cart, sections] = await Promise.all([
      fetchCart(),
      // No Accept: application/json here: /cart would answer with the cart JSON instead of
      // the sections. ROOT keeps the render in the visitor's locale (/en/, /de/).
      fetch(`${ROOT}cart?sections=${cartSectionsParam()}`)
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null),
    ]);
    applyCartState(cart, sections);
    return cart;
  };

  // Bundle upsell add (1-click, keeps drawer open).
  // Card markup is server-rendered; the section swap makes it disappear once the duo is complete.
  document.addEventListener("click", async (e) => {
    const btn = e.target.closest("[data-bundle-add]");
    if (!btn) return;
    const id = parseInt(btn.dataset.variant, 10);
    if (!Number.isFinite(id)) return;
    btn.disabled = true;
    btn.textContent = S.adding || "Ajout...";
    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id, quantity: 1, sections: cartSectionsParam(), sections_url: `${ROOT}cart` }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        if (data && data.description) showCartToast(data.description);
        btn.disabled = false;
        btn.textContent = S.bundleAdd || "Ajouter";
        return;
      }
      // /cart/add.js returns the added line, not the cart — fetch totals separately.
      const cart = await fetchCart();
      applyCartState(cart, data && data.sections);
      announceCart(cart);
    } catch (_) {
      showCartToast(S.addError || "Erreur, réessaye.");
      btn.disabled = false;
      btn.textContent = S.bundleAdd || "Ajouter";
    }
  });

  cartToggles.forEach((btn) =>
    btn.addEventListener("click", async (e) => {
      e.preventDefault();
      await syncCartFromServer();
      setDrawerOpen(drawer && drawer.dataset.open !== "true");
    })
  );

  document.addEventListener("click", (e) => {
    const target = e.target.closest("[data-cart-close]");
    if (target) setDrawerOpen(false);
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && drawer && drawer.dataset.open === "true") setDrawerOpen(false);
  });

  /* ---------- Cart drawer: swipe-to-close (touch devices) ---------- */
  // Two layouts: mobile (≤749px) is a bottom sheet — close gesture is swipe DOWN.
  //              desktop/tablet is a right-side drawer — close gesture is swipe RIGHT.
  // We pick the active axis at touchstart so device rotation between gestures is handled.
  // Drag follows the finger 1:1; snap-back if released before threshold.
  if (drawer) {
    const bottomSheetMQ = window.matchMedia("(max-width: 749px)"); // must match .cart-drawer bottom-sheet breakpoint in theme.css
    let touchStartX = 0;
    let touchStartY = 0;
    let touchStartTime = 0;
    let touchDelta = 0;
    let lastMoveTime = 0;
    let velocity = 0; // px/ms along the closing axis, from the last move sample
    let isDragging = false;
    let directionLocked = false;
    let axis = "x"; // 'x' = horizontal (right drawer), 'y' = vertical (bottom sheet)
    let drawerSize = 0;

    const onTouchStart = (e) => {
      if (drawer.dataset.open !== "true") return;
      // Ignore touches that start on interactive children (qty select, promo input, buttons,
      // remove icons, links) — user wants to interact, not swipe the drawer away.
      if (e.target.closest("select, input, textarea, button, a")) return;
      // Scroll-aware: on the bottom sheet (mobile), if the touch starts inside the scrollable
      // items list AND the list is already scrolled, let the list scroll naturally — don't init
      // a swipe-close. User can scroll the list up first; once at top, the next swipe down closes.
      // The header (with grab handle) bypasses this gate so users can always close from there.
      const isBottomSheet = bottomSheetMQ.matches;
      const startedInBody = drawerBody && drawerBody.contains(e.target);
      if (isBottomSheet && startedInBody && drawerBody.scrollTop > 0) return;
      const t = e.touches[0];
      touchStartX = t.clientX;
      touchStartY = t.clientY;
      touchStartTime = e.timeStamp;
      touchDelta = 0;
      lastMoveTime = 0;
      velocity = 0;
      isDragging = true;
      directionLocked = false;
      // Lock layout for this gesture (avoid mid-swipe viewport changes).
      axis = isBottomSheet ? "y" : "x";
      drawerSize = (axis === "y" ? drawer.offsetHeight : drawer.offsetWidth) || 1;
    };

    const onTouchMove = (e) => {
      if (!isDragging) return;
      const t = e.touches[0];
      const dx = t.clientX - touchStartX;
      const dy = t.clientY - touchStartY;
      const along = axis === "y" ? dy : dx;
      const cross = axis === "y" ? dx : dy;
      // Decide once whether this gesture matches our axis or is a cross-axis scroll.
      if (!directionLocked) {
        if (Math.abs(along) < 6 && Math.abs(cross) < 6) return; // wait for clearer intent
        directionLocked = true;
        // Abandon if (a) cross-axis dominates (likely a scroll inside drawer body), or
        // (b) along-axis is negative — wrong direction for close (swipe up on bottom sheet,
        // swipe left on right drawer). Letting go here means native scroll takes over so the
        // user can scroll the items list freely, even when touching near the drawer edges.
        if (Math.abs(cross) > Math.abs(along) || along < 0) {
          isDragging = false;
          return;
        }
      }
      // Only react to swipes in the closing direction (positive along-axis).
      const prevDelta = touchDelta;
      const prevTime = lastMoveTime || touchStartTime;
      touchDelta = Math.max(0, along);
      lastMoveTime = e.timeStamp;
      const dt = lastMoveTime - prevTime;
      if (dt > 0) velocity = (touchDelta - prevDelta) / dt;
      drawer.style.transition = "none";
      drawer.style.transform = axis === "y"
        ? `translate3d(0, ${touchDelta}px, 0)`
        : `translate3d(${touchDelta}px, 0, 0)`;
      if (drawerBackdrop) {
        drawerBackdrop.style.transition = "none";
        const remaining = Math.max(0, 1 - touchDelta / drawerSize);
        drawerBackdrop.style.opacity = String(remaining);
      }
      if (e.cancelable) e.preventDefault();
    };

    const resetDrawerStyles = () => {
      drawer.style.transition = "";
      drawer.style.transform = "";
      if (drawerBackdrop) {
        drawerBackdrop.style.transition = "";
        drawerBackdrop.style.opacity = "";
      }
    };

    const onTouchEnd = () => {
      if (!isDragging) return;
      isDragging = false;
      if (!directionLocked || touchDelta === 0) {
        resetDrawerStyles();
        return;
      }
      // Close on a fast flick (velocity past ~0.11 px/ms) even if short, or a slow drag
      // past 30% of the drawer size — distance alone missed fast flicks near the edge.
      const shouldClose = velocity > 0.11 || touchDelta > drawerSize * 0.3;
      resetDrawerStyles();
      if (shouldClose) setDrawerOpen(false);
    };

    drawer.addEventListener("touchstart", onTouchStart, { passive: true });
    drawer.addEventListener("touchmove", onTouchMove, { passive: false });
    drawer.addEventListener("touchend", onTouchEnd, { passive: true });
    drawer.addEventListener("touchcancel", onTouchEnd, { passive: true });
  }

  // JSON-template sections have ids like "template--123__main" — plain "main"
  // makes the Sections API return null. Read the real id from the DOM wrapper.
  const cartPageSectionId = () => {
    const root = document.querySelector("[data-cart-page-root]");
    const wrap = root && root.closest('[id^="shopify-section-"]');
    return wrap ? wrap.id.replace("shopify-section-", "") : "main";
  };

  // Swap [data-cart-page-root] with fresh HTML returned by Sections API.
  const swapCartPageSection = (html) => {
    if (!html) return;
    const root = document.querySelector("[data-cart-page-root]");
    if (!root) return;
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    const fresh = tmp.querySelector("[data-cart-page-root]");
    if (fresh) root.replaceWith(fresh);
  };

  // Apply a cart line change (absolute quantity) and sync UI.
  // Prefer the 1-based `line` index — bulletproof vs key encoding issues.
  // Request the rendered main-cart section so we can swap the cart page in place.
  const onCartPage = () => !!document.querySelector("[data-cart-page-root]");

  const changeCartLine = async ({ line, key, quantity, deferSwap }) => {
    const body = { quantity, sections: cartSectionsParam(), sections_url: `${ROOT}cart` };
    if (Number.isFinite(line) && line > 0) body.line = line;
    else if (key) body.id = key;
    else return null;
    try {
      const res = await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data) {
        const apply = () => {
          applyCartState(data, data.sections);
          announceCart(data);
          // Safety: on /cart with no rendered section returned, fall back to a full reload.
          if (onCartPage() && !(data.sections && data.sections[cartPageSectionId()])) {
            window.location.reload();
          }
        };
        if (deferSwap) return apply;
        apply();
        return null;
      } else if (data && data.description) {
        showCartToast(data.description);
        // Resync everything (drawer + page + bars) to server truth.
        await syncCartFromServer();
      }
    } catch (_) { /* silent */ }
    return null;
  };

  // Quantity dropdown — change event sends absolute quantity (no DOM math).
  document.addEventListener("change", (e) => {
    const sel = e.target.closest("[data-cart-qty-select]");
    if (!sel) return;
    const quantity = parseInt(sel.value, 10);
    if (!Number.isFinite(quantity) || quantity < 1) return;
    const line = parseInt(sel.dataset.line, 10);
    changeCartLine({ line, key: sel.dataset.key, quantity });
  });

  // Remove button — explicit deletion with fade/blur exit animation.
  document.addEventListener("click", (e) => {
    const removeBtn = e.target.closest("[data-cart-remove]");
    if (!removeBtn) return;
    const line = parseInt(removeBtn.dataset.line, 10);
    const key = removeBtn.dataset.key;
    const row = removeBtn.closest("[data-cart-line]");
    if (row) row.classList.add("is-removing");
    // Fire the mutation in parallel with the exit animation, then swap when both are done.
    const anim = new Promise((r) => setTimeout(r, 320));
    Promise.all([changeCartLine({ line, key, quantity: 0, deferSwap: true }), anim])
      .then(([applied]) => applied && applied());
  });

  // Promo: input stored in localStorage, prepended to checkout URL.
  // Multi-instance: drawer + cart page can both have a form, kept in sync.
  const PROMO_KEY = "sa_cart_promo";
  const sanitizePromo = (s) =>
    String(s || "").trim().toUpperCase().replace(/[^A-Z0-9_-]/g, "").slice(0, 64);

  const syncCheckoutLinks = (code) => {
    document.querySelectorAll("[data-cart-checkout]").forEach((a) => {
      a.href = code ? `/discount/${encodeURIComponent(code)}?redirect=/checkout` : "/checkout";
    });
  };

  const syncPromoUI = (code, statusText, statusState) => {
    document.querySelectorAll("[data-cart-promo-input]").forEach((inp) => { inp.value = code; });
    document.querySelectorAll("[data-cart-promo-form]").forEach((f) => {
      if (code) f.hidden = false;
    });
    document.querySelectorAll("[data-cart-promo-toggle]").forEach((t) => {
      if (code) t.setAttribute("aria-expanded", "true");
    });
    document.querySelectorAll("[data-cart-promo-status]").forEach((s) => {
      if (!statusText) { s.hidden = true; s.textContent = ""; s.removeAttribute("data-state"); return; }
      s.hidden = false;
      s.textContent = statusText;
      if (statusState) s.dataset.state = statusState; else s.removeAttribute("data-state");
    });
  };

  // /cart.js lists every entered code with its real status. Neither title matching
  // (cart.js has no discount_applications) nor total_discount (an automatic duo
  // discount counts too) can tell whether THIS code works.
  const isCodeActive = (cart, code) =>
    !!cart && (cart.discount_codes || []).some((d) => d.applicable && String(d.code || "").toUpperCase() === code);

  const applyPromo = async (raw) => {
    const code = sanitizePromo(raw);
    if (!code) {
      try { localStorage.removeItem(PROMO_KEY); } catch (_) {}
      syncCheckoutLinks(null);
      syncPromoUI("", S.promoCleared || "Code retiré.", "cleared");
      return;
    }
    syncPromoUI(code, S.promoApplying || "Application…", "pending");
    try {
      // Visiting /discount/CODE sets the discount cookie server-side, then redirects.
      // We follow the redirect to /cart.js so the response is small & we don't get HTML.
      await fetch(`/discount/${encodeURIComponent(code)}?redirect=/cart.js`, {
        method: "GET",
        credentials: "same-origin",
        redirect: "follow",
      });
    } catch (_) { /* silent */ }
    const fresh = await syncCartFromServer();
    const applied = isCodeActive(fresh, code);
    if (applied) {
      try { localStorage.setItem(PROMO_KEY, code); } catch (_) {}
      syncCheckoutLinks(code);
      syncPromoUI(code, (S.promoApplied || `Code « %CODE% » appliqué.`).replace("%CODE%", code), "ok");
    } else {
      try { localStorage.removeItem(PROMO_KEY); } catch (_) {}
      syncCheckoutLinks(null);
      syncPromoUI("", (S.promoInvalid || `Code « %CODE% » invalide ou non applicable.`).replace("%CODE%", code), "error");
    }
  };

  // Restore on load: verify the saved code is actually active server-side (cookie may have expired).
  const savedPromo = (() => {
    try { return sanitizePromo(localStorage.getItem(PROMO_KEY)); } catch (_) { return ""; }
  })();
  if (savedPromo) {
    fetchCart().then((cart) => {
      const stillApplied = isCodeActive(cart, savedPromo);
      if (stillApplied) {
        syncCheckoutLinks(savedPromo);
        syncPromoUI(savedPromo, (S.promoApplied || `Code « %CODE% » appliqué.`).replace("%CODE%", savedPromo), "ok");
      } else {
        try { localStorage.removeItem(PROMO_KEY); } catch (_) {}
        syncCheckoutLinks(null);
      }
    });
  }

  // Delegated handlers (work for drawer + cart page)
  document.addEventListener("click", (e) => {
    const toggle = e.target.closest("[data-cart-promo-toggle]");
    if (toggle) {
      const container = toggle.parentElement;
      const form = container ? container.querySelector("[data-cart-promo-form]") : null;
      if (!form) return;
      const open = form.hasAttribute("hidden");
      form.toggleAttribute("hidden", !open);
      toggle.setAttribute("aria-expanded", String(open));
      if (open) {
        const inp = form.querySelector("[data-cart-promo-input]");
        if (inp) inp.focus();
      }
      return;
    }
    const apply = e.target.closest("[data-cart-promo-apply]");
    if (apply) {
      const form = apply.closest("[data-cart-promo-form]");
      const inp = form ? form.querySelector("[data-cart-promo-input]") : null;
      applyPromo(inp ? inp.value : "");
    }
  });

  document.addEventListener("keydown", (e) => {
    if (e.key !== "Enter") return;
    const inp = e.target.closest && e.target.closest("[data-cart-promo-input]");
    if (!inp) return;
    e.preventDefault();
    applyPromo(inp.value);
  });

  // Cart page: checkout link must save pending qty changes first
  document.addEventListener("click", async (e) => {
    const link = e.target.closest("[data-cart-checkout]");
    if (!link || link.tagName !== "A") return;
    const form = link.closest('form[action$="/cart"]');
    if (!form) return; // drawer link: no form to flush
    e.preventDefault();
    const targetHref = link.href;
    try {
      const fd = new FormData(form);
      await fetch("/cart/update.js", { method: "POST", body: fd, headers: { Accept: "application/json" } });
    } catch (_) { /* proceed regardless */ }
    window.location.href = targetHref;
  });

  // Drawer, counts and discounts are server-rendered at page load — no initial
  // fetch needed. Only bfcache restores (back/forward) can show a stale cart.
  window.addEventListener("pageshow", (e) => {
    if (e.persisted) syncCartFromServer();
  });

  /* ---------- AJAX add-to-cart → open drawer ---------- */
  document.addEventListener("submit", async (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.matches('form[action$="/cart/add"]')) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    // Cache the canonical label once on the button itself — robust across multiple adds.
    if (btn && !btn.dataset.labelOriginal) {
      btn.dataset.labelOriginal = btn.dataset.labelAvailable || btn.textContent.trim() || "Ajouter au panier";
    }
    const restoreBtn = () => {
      if (!btn) return;
      btn.disabled = false;
      btn.textContent = btn.dataset.labelOriginal;
    };
    if (btn) { btn.disabled = true; btn.textContent = S.adding || "Ajout..."; }
    try {
      const fd = new FormData(form);
      const variantId = parseInt(fd.get("id"), 10);
      const addQty = parseInt(fd.get("quantity"), 10) || 1;
      const invEl = form.querySelector("[data-variant-inventory]");
      if (invEl && Number.isFinite(variantId)) {
        let inv = null;
        try { inv = JSON.parse(invEl.textContent); } catch (_) {}
        const v = inv && inv[variantId];
        if (v && v.tracked && v.policy === "deny") {
          const stock = v.qty;
          const cart = await fetchCart();
          const existing = cart && cart.items
            ? cart.items.filter((it) => it.variant_id === variantId).reduce((s, it) => s + it.quantity, 0)
            : 0;
          if (existing + addQty > stock) {
            const remaining = Math.max(0, stock - existing);
            showCartToast(
              remaining > 0
                ? (S.stockLimited || `Stock limité : il reste %N% article(s).`).replace("%N%", remaining)
                : (S.outOfStock || "Stock épuisé pour cet article.")
            );
            restoreBtn();
            return;
          }
        }
      }
      fd.append("sections", cartSectionsParam());
      fd.append("sections_url", `${ROOT}cart`);
      const res = await fetch("/cart/add.js", { method: "POST", body: fd, headers: { Accept: "application/json" } });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        if (data && data.description) showCartToast(data.description);
        return;
      }
      // /cart/add.js returns the added line, not the cart — fetch totals separately.
      const cart = await fetchCart();
      applyCartState(cart, data && data.sections);
      if (cart) setDrawerOpen(true);
    } catch (err) {
      showCartToast(S.addError || "Erreur, réessaye.");
    } finally {
      restoreBtn();
    }
  });

  /* ---------- Live search (predictive) ---------- */
  const searchInput = document.getElementById("q");
  const searchResults = document.getElementById("search-live-results");
  if (searchInput && searchResults) {
    let debounceTimer = null;
    let activeIndex = -1;

    const formatMoney = (cents) => {
      return (cents / 100).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
    };

    const getItems = () => searchResults.querySelectorAll(".search-live-item");

    // Combobox contract: the input owns the state, aria-activedescendant is what
    // makes the highlighted option actually get read out.
    const setActive = (index) => {
      const items = getItems();
      items.forEach((el, i) => {
        el.setAttribute("aria-selected", i === index ? "true" : "false");
      });
      if (index >= 0 && items[index]) searchInput.setAttribute("aria-activedescendant", items[index].id);
      else searchInput.removeAttribute("aria-activedescendant");
      activeIndex = index;
    };

    let closeTimer = null;
    const showResults = () => {
      clearTimeout(closeTimer);
      searchResults.hidden = false;
      searchInput.setAttribute("aria-expanded", "true");
      requestAnimationFrame(() => searchResults.classList.add("is-open"));
    };
    const hideResults = () => {
      searchResults.classList.remove("is-open");
      searchInput.setAttribute("aria-expanded", "false");
      searchInput.removeAttribute("aria-activedescendant");
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => { searchResults.hidden = true; }, 200);
    };

    const closeResults = () => {
      hideResults();
      activeIndex = -1;
    };

    const renderResults = (products, query) => {
      if (!products.length) {
        searchResults.innerHTML = `<p class="search-live-empty">${(S.searchNoResults || `Aucun résultat pour "%Q%"`).replace("%Q%", escapeHtml(query))}</p>`;
      } else {
        const items = products.map((p, i) => {
          let rawSrc = null;
          if (typeof p.featured_image === "string") rawSrc = p.featured_image;
          else if (p.featured_image && typeof p.featured_image.url === "string") rawSrc = p.featured_image.url;
          else if (p.image && typeof p.image.src === "string") rawSrc = p.image.src;
          else if (p.image && typeof p.image.url === "string") rawSrc = p.image.url;
          const imgSrc = rawSrc ? (rawSrc.indexOf("?") >= 0 ? rawSrc + "&width=112" : rawSrc + "?width=112") : null;
          const img = imgSrc ? `<img src="${escapeHtml(imgSrc)}" alt="" loading="lazy">` : `<div class="search-live-img-placeholder"></div>`;
          const price = p.price ? formatMoney(p.price) : "";
          return `<a href="${escapeHtml(p.url)}" id="search-opt-${i}" class="search-live-item" role="option" aria-selected="false">${img}<span class="search-live-item-info"><span class="search-live-item-title">${escapeHtml(p.title)}</span><span class="search-live-item-price">${price}</span></span></a>`;
        }).join("");
        searchResults.innerHTML = items + `<div class="search-live-footer"><a href="${ROOT}search?q=${encodeURIComponent(query)}">${S.searchViewAll || "Voir tous les résultats →"}</a></div>`;
      }
      showResults();
      activeIndex = -1;
    };

    const fetchResults = async (query) => {
      try {
        const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`;
        const res = await fetch(url, { headers: { Accept: "application/json" } });
        if (!res.ok) return;
        const data = await res.json();
        const products = data.resources?.results?.products || [];
        renderResults(products, query);
      } catch (_) { /* silent */ }
    };

    searchInput.addEventListener("input", () => {
      clearTimeout(debounceTimer);
      const q = searchInput.value.trim();
      if (q.length < 2) { closeResults(); return; }
      debounceTimer = setTimeout(() => fetchResults(q), 280);
    });

    searchInput.addEventListener("keydown", (e) => {
      const items = getItems();
      if (!items.length) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActive(Math.min(activeIndex + 1, items.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive(Math.max(activeIndex - 1, 0));
      } else if (e.key === "Enter" && activeIndex >= 0) {
        e.preventDefault();
        items[activeIndex].click();
      } else if (e.key === "Escape") {
        closeResults();
      }
    });

    document.addEventListener("click", (e) => {
      if (!searchInput.contains(e.target) && !searchResults.contains(e.target)) closeResults();
    });
  }

  /* ---------- PDP variant selector ---------- */
  document.querySelectorAll("[data-pdp-form]").forEach((form) => {
    const jsonEl = form.querySelector("[data-product-json]");
    if (!jsonEl) return;
    let product;
    try { product = JSON.parse(jsonEl.textContent); } catch (_) { return; }
    const variants = product.variants || [];
    if (!variants.length) return;

    const currency = form.dataset.currency || "EUR";
    const idInput = form.querySelector("[data-pdp-id]") || form.querySelector('input[name="id"]');
    const addBtn = form.querySelector("[data-pdp-add]");
    // Flag on <body>, hiding done in CSS (theme.css) — the app injects its button
    // async and rewrites its own inline display, so an inline style here loses the race.
    const toggleNotify = (avail) => {
      document.body.dataset.variantAvailable = avail ? "true" : "false";
    };
    const priceScope = form.closest(".pdp__col") || document;
    const priceEl = priceScope.querySelector("[data-pdp-price]");
    const compareEl = priceScope.querySelector("[data-pdp-compare]");
    const swatches = form.querySelectorAll('.pdp__swatch input[type="radio"]');
    const lowStockEl = (form.closest(".pdp__col") || document).querySelector("[data-pdp-lowstock]");
    const LOW_STOCK_THRESHOLD = 5;
    let inventory = {};
    const invJsonEl = form.querySelector("[data-variant-inventory]");
    if (invJsonEl) { try { inventory = JSON.parse(invJsonEl.textContent); } catch (_) {} }

    // Sticky add-to-cart bar (mobile) — mirrors this form's CTA
    const sticky = document.querySelector("[data-pdp-sticky]");
    const stickyPrice = sticky && sticky.querySelector("[data-sticky-price]");
    const stickyAdd = sticky && sticky.querySelector("[data-sticky-add]");
    const optionCount = swatches.length ? (product.options || []).length : 0;
    // Until every option is picked, nothing is added — not even by the sticky bar.
    const isIncomplete = (sel) => { for (let i = 0; i < optionCount; i++) if (sel[i] == null) return true; return false; };

    const fmt = (cents) => (cents / 100).toLocaleString("fr-FR", { style: "currency", currency });

    const getSelected = () => {
      const out = [];
      swatches.forEach((inp) => {
        if (inp.checked) out[parseInt(inp.dataset.optionIndex, 10)] = inp.value;
      });
      return out;
    };

    const findVariant = (sel) => {
      if (!sel.length) return variants[0];
      return variants.find((v) => v.options.every((o, i) => o === sel[i]));
    };

    const updateDisabled = (sel) => {
      swatches.forEach((inp) => {
        const idx = parseInt(inp.dataset.optionIndex, 10);
        const val = inp.value;
        const matchable = variants.some((v) =>
          v.options[idx] === val &&
          v.options.every((o, i) => i === idx || sel[i] == null || o === sel[i])
        );
        const hasAvail = variants.some((v) =>
          v.available && v.options[idx] === val &&
          v.options.every((o, i) => i === idx || sel[i] == null || o === sel[i])
        );
        const label = inp.closest(".pdp__swatch");
        if (!label) return;
        label.classList.toggle("pdp__swatch--disabled", !hasAvail);
        inp.disabled = !matchable;
      });
    };

    const update = () => {
      const sel = getSelected();
      const incomplete = isIncomplete(sel);
      form.classList.toggle("is-unselected", incomplete);
      if (incomplete) {
        if (addBtn) {
          addBtn.disabled = true;
          addBtn.textContent = addBtn.dataset.labelChoose || addBtn.textContent;
        }
        toggleNotify(true); // no variant yet: keep the back-in-stock button hidden
        if (lowStockEl) lowStockEl.hidden = true;
        // Stays enabled: a tap scrolls to the size picker instead of adding.
        if (stickyAdd) {
          stickyAdd.disabled = false;
          stickyAdd.textContent = stickyAdd.dataset.labelAvailable || stickyAdd.textContent;
        }
        updateDisabled(sel);
        return;
      }
      const variant = findVariant(sel);
      if (variant) {
        if (idInput) idInput.value = variant.id;
        if (priceEl) priceEl.textContent = fmt(variant.price);
        if (compareEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            compareEl.textContent = fmt(variant.compare_at_price);
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }
        if (addBtn) {
          addBtn.disabled = !variant.available;
          addBtn.textContent = variant.available
            ? (addBtn.dataset.labelAvailable || addBtn.textContent)
            : (addBtn.dataset.labelSoldout || "Sold out");
        }
        toggleNotify(variant.available);
        // Low stock notice (real inventory, threshold 5)
        if (lowStockEl) {
          const inv = inventory[variant.id];
          if (variant.available && inv && inv.tracked && inv.qty > 0 && inv.qty <= LOW_STOCK_THRESHOLD) {
            const tpl = lowStockEl.dataset.labelTemplate || "Plus que __N__ en stock";
            lowStockEl.textContent = tpl.replace("__N__", inv.qty);
            lowStockEl.hidden = false;
          } else {
            lowStockEl.hidden = true;
          }
        }
        // Sticky bar sync
        if (stickyPrice) stickyPrice.textContent = fmt(variant.price);
        if (stickyAdd) {
          stickyAdd.disabled = !variant.available;
          stickyAdd.textContent = variant.available
            ? (stickyAdd.dataset.labelAvailable || stickyAdd.textContent)
            : (stickyAdd.dataset.labelSoldout || "Sold out");
        }
      } else {
        if (addBtn) {
          addBtn.disabled = true;
          addBtn.textContent = addBtn.dataset.labelUnavailable || "Unavailable";
        }
        toggleNotify(false);
        if (lowStockEl) lowStockEl.hidden = true;
        if (stickyAdd) stickyAdd.disabled = true;
      }
      updateDisabled(sel);
    };

    form.addEventListener("change", (e) => {
      if (e.target.matches('input[type="radio"][name^="options["]')) update();
    });
    update();

    // Sticky bar: reveal when the main CTA scrolls out of view (mobile-only via CSS)
    if (sticky && addBtn && "IntersectionObserver" in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          const show = !e.isIntersecting;
          sticky.hidden = !show;
          sticky.setAttribute("aria-hidden", show ? "false" : "true");
          // The bar keeps display:block while [hidden] so it can slide out, so
          // [hidden] alone leaves its button tabbable off-screen.
          sticky.toggleAttribute("inert", !show);
        });
      }, { rootMargin: "0px 0px -10% 0px" });
      io.observe(addBtn);
    }
    if (stickyAdd && addBtn) {
      const optionsEl = form.querySelector(".pdp__options");
      // No size picked yet: bring the picker into view so the user chooses consciously.
      stickyAdd.addEventListener("click", () => {
        if (stickyAdd.disabled) return;
        if (optionsEl && isIncomplete(getSelected())) {
          optionsEl.scrollIntoView({ behavior: "smooth", block: "center" });
          optionsEl.classList.add("pdp__options--pulse");
          setTimeout(() => optionsEl.classList.remove("pdp__options--pulse"), 1200);
          return;
        }
        // Every option picked (or single-variant) → add directly.
        if (typeof form.requestSubmit === "function") form.requestSubmit(addBtn);
        else addBtn.click();
      });
    }

  });

  /* ---------- PDP: gallery dots scroll-sync (markup: main-product.liquid) ---------- */
  (function () {
    var track = document.querySelector('.pdp__main');
    var dots = document.querySelectorAll('.pdp__dot');
    if (!track || !dots.length) return;
    track.addEventListener('scroll', function () {
      var idx = Math.round(track.scrollLeft / track.offsetWidth);
      dots.forEach(function (d, i) { d.classList.toggle('is-active', i === idx); });
    }, { passive: true });
  })();

  /* ---------- PDP: RestockRocket FR relabel ----------
     The app injects its button after load with an EN label (its
     storefront_button_text setting, multi-language off), and re-renders it later
     (variant change, settings fetch), which puts the EN label back — sometimes by
     replacing its whole container, which a container-scoped observer misses. So the
     body observer stays on for the page's life, batched to one scan per task. */
  (function () {
    var pdp = document.querySelector('.pdp');
    if (!pdp) return; // product pages only
    // Coming-soon drops carry their own localized label ("S'inscrire au drop").
    var dropLabel = pdp.getAttribute('data-notify-label');
    var lang = (document.documentElement.lang || '').split('-')[0];
    if (lang !== 'fr' && !dropLabel) return;
    var FR = dropLabel ? dropLabel.toUpperCase() : 'ME PREVENIR QUAND L’ARTICLE EST DISPONIBLE';
    function translateRestockBtn(btn) {
      for (var i = 0; i < btn.childNodes.length; i++) {
        var node = btn.childNodes[i];
        // Skip when already FR — rewriting would retrigger our own observer.
        if (node.nodeType === Node.TEXT_NODE && node.textContent.trim() && node.textContent.trim() !== FR) {
          node.textContent = ' ' + FR;
        }
      }
    }
    function scanButtons() {
      document.querySelectorAll('.restock-rocket-button-product').forEach(translateRestockBtn);
    }
    var queued = false;
    var obs = new MutationObserver(function () {
      if (queued) return;
      queued = true;
      setTimeout(function () { queued = false; scanButtons(); }, 0); // not rAF: paused in background tabs
    });
    obs.observe(document.body, { childList: true, subtree: true, characterData: true });
    scanButtons();
  })();

  /* ---------- Size calculator modal (markup: snippets/size-calculator.liquid) ---------- */
  (function () {
    var root = document.querySelector('[data-szc]');
    if (!root) return;

    var SCALE = ['XS', 'S', 'M', 'L', 'XL', '2XL'];
    var COUPE_SHIFT = { slim: -1, normal: 0, oversize: 1 };
    var mode = root.getAttribute('data-szc-mode'); // 'top' | 'bottom'
    var hfmt = root.getAttribute('data-szc-hfmt'); // 'fr' | 'cm'
    var tallFit = root.getAttribute('data-szc-tallfit') === 'true';
    var available = (root.getAttribute('data-szc-sizes') || '').split(',').map(function (s) { return s.trim(); });

    var heightIn = root.querySelector('[data-szc-height]');
    var weightIn = root.querySelector('[data-szc-weight]');
    var heightOut = root.querySelector('[data-szc-height-out]');
    var weightOut = root.querySelector('[data-szc-weight-out]');
    var coupeBtns = root.querySelectorAll('[data-szc-coupe]');
    var stepForm = root.querySelector('[data-szc-step="form"]');
    var stepResult = root.querySelector('[data-szc-step="result"]');
    var resultEl = root.querySelector('[data-szc-result]');
    var specEl = root.querySelector('[data-szc-spec]');
    var applyBtn = root.querySelector('[data-szc-apply]');
    var sizeOptName = root.getAttribute('data-szc-optname') || '';
    var coupe = 'normal';

    function fmtHeight(cm) {
      if (hfmt === 'cm') return cm + ' cm';
      var m = Math.floor(cm / 100), r = cm % 100;
      return m + 'm' + String(r).padStart(2, '0');
    }
    function syncOut() {
      if (heightIn && heightOut) heightOut.textContent = fmtHeight(+heightIn.value);
      if (weightIn && weightOut) weightOut.textContent = weightIn.value + 'kg';
    }
    if (heightIn) {
      heightIn.addEventListener('input', syncOut);
      if (weightIn) weightIn.addEventListener('input', syncOut);
      syncOut();

      coupeBtns.forEach(function (b) {
        b.addEventListener('click', function () {
          coupe = b.getAttribute('data-szc-coupe');
          coupeBtns.forEach(function (x) { x.classList.toggle('is-active', x === b); x.setAttribute('aria-pressed', x === b); });
        });
      });
    }

    // Tops: height (cm) decides the size — brand's reference grading (XS 155-165,
    // S 166-175, M 176-181, L 182-186, XL 187+).
    function topIndex(h) {
      if (h < 166) return 0; if (h < 176) return 1; if (h < 182) return 2;
      if (h < 187) return 3; return 4;
    }
    // Bottoms: weight-driven.
    function bottomIndex(w) {
      if (w < 60) return 0; if (w < 72) return 1; if (w < 84) return 2;
      if (w < 95) return 3; return 4;
    }
    // This product's tall-fit override (tag 'sizing:tall-fit'): height-driven
    // like tops, but graded taller (L holds through 190cm, not 187) per its
    // actual fit — e.g. 1m80/70kg -> M, 1m85/80kg -> L, 1m90/85kg -> L
    // (oversize -> XL), 1m60/55-60kg -> XS.
    function tallFitIndex(h) {
      if (h < 166) return 0; if (h < 176) return 1; if (h < 182) return 2;
      if (h < 191) return 3; return 4;
    }
    // Plaid jackets: height only, runs small (XS 155-165, S 166-174, M 175-180,
    // L 181-185, XL 186+). No weight, no fit option.
    function plaidIndex(h) {
      if (h < 166) return 0; if (h < 175) return 1; if (h < 181) return 2;
      if (h < 186) return 3; return 4;
    }
    // Weight correction for tops, relative to the average build for that height
    // (BMI 22 reference: 22 * m^2). Progressive, no cap: heavier bodies climb
    // multiple sizes (~+1 per 12kg over) so a 105kg/1m75 build lands on XL, not M.
    // Slim only goes a size tighter when there is slack: body lighter than the
    // average for its height. Anyone at or above it keeps their physical-fit size.
    function lighterThanAverage(h, w) { return w < 22 * Math.pow(h / 100, 2); }
    function weightNudge(h, w) {
      var dev = w - 22 * Math.pow(h / 100, 2);
      if (dev > 0) return Math.floor(dev / 12);   // up: ~+1 size per 12kg over
      return Math.ceil(dev / 18);                  // down: ~-1 size per 18kg under
    }

    // Map a canonical size to the nearest size actually offered by this product.
    function mapAvailable(label) {
      if (available.indexOf(label) >= 0) return label;
      var ci = SCALE.indexOf(label), best = available[0], bd = 99;
      available.forEach(function (s) {
        var i = SCALE.indexOf(s);
        if (i >= 0 && Math.abs(i - ci) < bd) { bd = Math.abs(i - ci); best = s; }
      });
      return best;
    }

    function recommend(h, w, fit) {
      var idx;
      if (mode === 'plaid') {
        idx = plaidIndex(h);
      } else if (mode === 'bottom' && tallFit) {
        var nudgeB = weightNudge(h, w);
        var fitIdxB = tallFitIndex(h) + nudgeB;
        if (fit === 'oversize') {
          idx = fitIdxB + 1;
        } else if (fit === 'slim') {
          idx = fitIdxB - (lighterThanAverage(h, w) ? 1 : 0);
        } else {
          idx = fitIdxB;
        }
      } else if (mode === 'bottom') {
        idx = bottomIndex(w) + (fit === 'slim' && !lighterThanAverage(h, w) ? 0 : COUPE_SHIFT[fit]);
      } else {
        // fitIdx = smallest size the body actually fits into (= normal fit).
        var nudge = weightNudge(h, w);
        var fitIdx = topIndex(h) + nudge;
        if (fit === 'oversize') {
          idx = fitIdx + 1;
        } else if (fit === 'slim') {
          idx = fitIdx - (lighterThanAverage(h, w) ? 1 : 0);
        } else {
          idx = fitIdx;
        }
      }
      idx = Math.max(0, Math.min(SCALE.length - 1, idx));
      return mapAvailable(SCALE[idx]);
    }

    // The PDP swatch matching a recommended size, when this product exposes one.
    function swatchFor(size) {
      if (!sizeOptName || !size) return null;
      var name = 'options[' + sizeOptName + ']';
      var inputs = document.querySelectorAll('.pdp__swatch input[type="radio"]');
      for (var i = 0; i < inputs.length; i++) {
        if (inputs[i].name === name && inputs[i].value === size) return inputs[i];
      }
      return null;
    }

    // Replay the measurements behind the verdict: 1M75 · 70KG · NORMAL.
    function specLine() {
      var parts = [];
      if (heightOut) parts.push(heightOut.textContent);
      if (weightOut) parts.push(weightOut.textContent);
      var activeCoupe = root.querySelector('[data-szc-coupe].is-active');
      if (activeCoupe && weightIn) parts.push(activeCoupe.textContent);
      return parts.join(' · ');
    }

    var calcBtn = root.querySelector('[data-szc-calc]');
    if (calcBtn) {
      calcBtn.addEventListener('click', function () {
        var size = recommend(+heightIn.value, weightIn ? +weightIn.value : 0, coupe);
        resultEl.textContent = size;
        if (specEl) specEl.textContent = specLine();
        if (applyBtn) {
          var target = swatchFor(size);
          applyBtn.hidden = !target;
          if (target) {
            applyBtn.textContent = (applyBtn.getAttribute('data-szc-apply-label') || '').replace('%%SIZE%%', size);
          }
        }
        stepResult.classList.add('is-leaving');
        stepForm.classList.add('is-leaving');
        setTimeout(function () {
          stepForm.hidden = true;
          stepForm.classList.remove('is-leaving');
          stepResult.hidden = false;
          resultEl.classList.remove('is-locked');
          requestAnimationFrame(function () {
            stepResult.classList.remove('is-leaving');
            void resultEl.offsetWidth; // reflow so the lock replays on every recalculation
            resultEl.classList.add('is-locked');
          });
        }, 180);
      });
    }

    // Finish the job: select the recommended size on the product form, then step out.
    if (applyBtn) {
      applyBtn.addEventListener('click', function () {
        var target = swatchFor((resultEl.textContent || '').trim());
        if (target) {
          target.checked = true;
          target.dispatchEvent(new Event('change', { bubbles: true }));
        }
        close();
      });
    }

    var modalCloseTimer = null;
    function open() {
      if (!root.hidden) return;
      clearTimeout(modalCloseTimer);
      root.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      if (stepForm) { stepForm.hidden = false; stepForm.classList.remove('is-leaving'); }
      if (stepResult) { stepResult.hidden = true; stepResult.classList.remove('is-leaving'); }
      if (resultEl) resultEl.classList.remove('is-locked');
      requestAnimationFrame(function () { root.classList.add('is-open'); });
      openPanel(root); // moves focus into the dialog, inerts the page behind it
    }
    function close() {
      if (root.hidden) return;
      root.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      // [hidden] is display:none here, so the dialog leaves the tab order on its own.
      closePanel(root, false);
      clearTimeout(modalCloseTimer);
      modalCloseTimer = setTimeout(function () { root.hidden = true; }, 260);
    }

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-szc-open]')) { e.preventDefault(); open(); }
      else if (e.target.closest('[data-szc-close]')) { close(); }
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !root.hidden) close(); });
  })();

  /* ---------- Welcome popup (markup: sections/welcome-popup.liquid) ---------- */
  (function () {
    var KEY = 'sidearts_welcome_seen_v1';
    var popup = document.getElementById('welcome-popup');
    if (!popup) return;

    var inEditor = !!(window.Shopify && window.Shopify.designMode);
    var justPosted = !!popup.querySelector('[data-wp-success]');
    var hasServerError = !!popup.querySelector('[data-wp-server-error]');
    // Storage can throw when site data is blocked: the popup must still work, it just won't remember.
    var alreadySeen = false;
    try { alreadySeen = localStorage.getItem(KEY) === '1'; } catch (_) {}
    function markSeen() { try { localStorage.setItem(KEY, '1'); } catch (_) {} }

    var stateForm = popup.querySelector('[data-state="form"]');
    var stateSuccess = popup.querySelector('[data-state="success"]');
    var closeBtn = popup.querySelector('.wp__close');
    var copyBtn = popup.querySelector('.wp__copy');

    function open() {
      if (!popup.classList.contains('is-closed')) return;
      popup.classList.remove('is-closed');
      document.body.classList.add('no-scroll');
      openPanel(popup);
    }
    function close() {
      if (popup.classList.contains('is-closed')) return;
      popup.classList.add('is-closed');
      document.body.classList.remove('no-scroll');
      // .is-closed is visibility:hidden — already out of the tab order, so no
      // inert: Shopify's bot protection still mounts on the form underneath.
      closePanel(popup, false);
      markSeen();
    }

    closeBtn.addEventListener('click', close);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });

    // Popup is server-rendered with .is-closed (invisible but DOM-attached so bot protection / captchas
    // can mount on the form). We selectively reveal it based on context.
    if (justPosted) {
      // Form was just submitted successfully — show success state immediately.
      stateForm.hidden = true;
      stateSuccess.hidden = false;
      open();
      markSeen();
    } else if (hasServerError || inEditor) {
      // Show immediately so user sees the error / editor preview works.
      open();
    } else if (!alreadySeen) {
      // First visit — open after configured delay. No flash because popup starts hidden server-side.
      var delay = parseInt(popup.dataset.delay, 10);
      if (isNaN(delay) || delay < 0) delay = 2000;
      // Never stack on Shopify's cookie-consent banner: it is fixed at z-index 2000000,
      // so the two overlays sandwich the page CTA. Check for the banner element when the
      // delay elapses — Shopify.trackingConsent loads async and is often still undefined
      // at this point, so the consent API can't be trusted here.
      setTimeout(function () {
        if (!document.querySelector('.shopify-pc__banner__dialog')) { open(); return; }
        document.addEventListener('visitorConsentCollected', function () {
          setTimeout(open, 400);
        }, { once: true });
      }, delay);
    }

    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        var code = copyBtn.dataset.code || '';
        var done = function () {
          copyBtn.classList.add('is-copied');
          setTimeout(function () { copyBtn.classList.remove('is-copied'); }, 1600);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(done).catch(function () {
            fallbackCopy(code); done();
          });
        } else {
          fallbackCopy(code); done();
        }
      });
    }

    function fallbackCopy(text) {
      var ta = document.createElement('textarea');
      ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'absolute'; ta.style.left = '-9999px';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch (e) {}
      document.body.removeChild(ta);
    }
  })();
})();
